<script setup lang="ts">
import type { SectionBaseItem } from '~/models/Content/SectionBaseItem'
import type { ContentSubjectHeader } from '~/models/Content/ContentSubjectHeader'
import type { ContentProduct } from '~/models/Content/ContentProduct'
import { computed, onBeforeMount, onBeforeUnmount, onMounted, watch } from 'vue'
import { storeToRefs } from 'pinia'
import { useQuery } from '@tanstack/vue-query'
import { setTitle } from '~/utils/dom'
import useSubjectsStore from '~/stores/subjects'
import useProductStore from '~/stores/product'
import useFilterSettingsStore from '~/stores/filterSettings'
import useFilterStore from '~/stores/filter'
import { useAuthStore } from '~/stores/auth'
import { ContentType } from '~/models/Content/ContentType'
import useQueryFilter from '~/composables/useQueryFilter'
import useContentApi from '~/api/contentApi'
import TeacherResources from '~/components/ResourceFinder/TeacherResources.vue'
import ResourceFinder from '~/components/ResourceFinder/ResourceFinder.vue'
import SectionRenderer from '~/components/ResourceEngine/SectionRenderer.vue'
import ProductBanner from '~/components/banners/ProductBanner.vue'

const props = defineProps<{ product?: ContentProduct }>()

const { setCurrentSubject } = useSubjectsStore()
const { setFilterOptions, resetFilterOptions } = useFilterSettingsStore()
const { isTeacher, isAuthenticated } = storeToRefs(useAuthStore())
const { findContents } = useContentApi()

const productStore = useProductStore()
const { hasLoaded: hasLoadedProducts } = storeToRefs(productStore)

const filterStore = useFilterStore()
const { setSelectedAddonLocationId, filterWatchers, $reset } = filterStore
const { selectedHeaders, selectedGrade } = storeToRefs(filterStore)

const watchers = filterWatchers()

const firstBanner = computed(() => (productData?.value ?? [])[0])
const relevantBanner = computed(() => productData?.value
  ?.find(header => selectedGrade.value && header.grades.includes(selectedGrade.value)) ?? firstBanner.value)

const banner = computed(() => ({
  title: props.product?.title,
  text: props.product?.description,
  image: relevantBanner.value?.image,
}))

const { data: sections } = useQuery({
  enabled: computed(() => isAuthenticated.value && !!props.product?.pathString),
  queryKey: computed(() => ['legacy_sections', props.product?.pathString]),
  queryFn: () => findContents<SectionBaseItem>({
    subtreeCriterion: [props.product!.pathString],
    sortField: 'priority',
    sortOrder: 'asc',
    contentTypeCriterion: [
      ContentType.SectionBox,
      ContentType.SectionExpanded,
      ContentType.SectionStandalone,
    ]
  }),
  staleTime: Infinity,
})

const { data: productData } = useQuery({
  enabled: computed(() => isAuthenticated.value && !!props.product?.pathString),
  queryKey: computed(() => ['legacy_data', props.product?.pathString]),
  queryFn: () => findContents<ContentSubjectHeader>({
    subtreeCriterion: [props.product!.pathString],
    contentTypeCriterion: [ContentType.SubjectHeader],
    sortField: 'priority',
    sortOrder: 'desc',
  })
})

onBeforeMount(() => setFilterOptions({ subject: false, grade: true, header: true }))

onMounted(() => useQueryFilter())

onBeforeUnmount(() => {
  productData.value = []
  setSelectedAddonLocationId(undefined)
  resetFilterOptions()
  watchers.stopFilterWatchers()
  $reset()
})

watch(props, () => {
  if (!props.product)
    return
  setCurrentSubject(undefined)
  setTitle(props.product.title)
  if (isAuthenticated.value)
    setSelectedAddonLocationId(props.product.locationId)
}, { immediate: true })
</script>

<template>
  <ProductBanner
    :title="banner.title"
    :image="banner.image"
    :text="banner.text"
  />
  <ResourceFinder
    show-package-intro
    :enable-subject-filter="false"
  />
  <TeacherResources
    v-if="isTeacher && hasLoadedProducts && !selectedHeaders.length"
    :location-id="product?.locationId"
  />
  <SectionRenderer
    v-if="sections && sections.length && !selectedHeaders.length"
    :items="sections"
  />
</template>
